/**
 * Asset Page
 * Full-screen layout matching original: Map (left) + Detail sidebar (right) + Timeline (bottom)
 * Edit via dialog triggered by pencil icon.
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Snackbar,
  Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  ChevronLeft as BackIcon,
  Edit as EditIcon,
} from '@mui/icons-material';

import AppBar from '../elements/AppBar';
import AsseTracMap from '../elements/maps/AsseTracMap';
import MapOverlaysPanel from '../elements/MapOverlaysPanel';
import AssetTimeline from '../elements/AssetTimeline';
import RetireAssetDialog from '../elements/RetireAssetDialog';
import AreaEventsLog from '../elements/AreaEventsLog';
import useProjectSelector from '../../hooks/useProjectSelector';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchAsset, fetchAssetHistory, updateAssetAction, fetchAssetTypes, fetchAssetSubTypes } from '../../store/slices/assetSlice';
import { setAssetsData, setMovesData, setOverlaysData, setProjectBoundariesData, updateViewport, clearAssetDetailState, setVisibleOverlays, toggleOverlay } from '../../store/slices/mapSlice';
import { fetchProjectsByCustomer } from '../../store/slices/projectSlice';
import { colorPalette } from '../../theme';
import { formatDateTime, safeGet } from '../../utils/helpers';
import * as api from '../../services/api';

const SIDEBAR_WIDTH = 400;

const AssetPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { assetList, projectAssetList, assetHistoryList, assetTypes: assetTypeList, assetSubTypes: assetSubtypeList } = useAppSelector((state) => state.asset);
  const { projectList } = useAppSelector((state) => state.project);
  const { activeCustomer } = useAppSelector((state) => state.customer);
  const { ProjectSelectorComponent } = useProjectSelector();

  const [asset, setAsset] = useState(null);
  const [assetOwners, setAssetOwners] = useState([]);

  const uniqueSuppliers = useMemo(() => {
    const set = new Set();
    for (const a of [...assetList, ...projectAssetList]) {
      if (a.supplier) set.add(a.supplier);
    }
    return [...set].sort();
  }, [assetList, projectAssetList]);

  const uniqueDimensions = useMemo(() => {
    const set = new Set();
    for (const a of [...assetList, ...projectAssetList]) {
      if (a.dimensions) set.add(a.dimensions);
    }
    return [...set].sort();
  }, [assetList, projectAssetList]);

  const uniqueOwnerNames = useMemo(() => {
    const set = new Set();
    for (const a of [...assetList, ...projectAssetList]) {
      const name = typeof a.owner === 'object' ? a.owner?.name : a.owner;
      if (name) set.add(name);
    }
    return [...set].sort();
  }, [assetList, projectAssetList]);

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [retireDialogOpen, setRetireDialogOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [visibleWaypoints, setVisibleWaypoints] = useState([]);
  const [overlaysPanelOpen, setOverlaysPanelOpen] = useState(false);
  const [projectAreas, setProjectAreas] = useState([]);
  const visibleOverlays = useAppSelector((state) => state.map.visibleOverlays);

  // Clean up asset-detail-specific data when leaving this page
  useEffect(() => {
    return () => {
      dispatch(clearAssetDetailState());
    };
  }, [dispatch]);

  // Load asset
  useEffect(() => {
    const loadAsset = async () => {
      setLoading(true);
      let foundAsset = assetList.find(a => a.id === id) ||
                       projectAssetList.find(a => a.id === id);
      if (!foundAsset) {
        try {
          const result = await dispatch(fetchAsset(id)).unwrap();
          foundAsset = result;
        } catch (err) {
          console.error('Error fetching asset:', err);
        }
      }
      setAsset(foundAsset);
      setLoading(false);
      if (foundAsset) {
        const now = Math.floor(Date.now() / 1000);
        const isGateway = foundAsset.deviceType === 'gateway' ||
          foundAsset.deviceTypeId === 'gateway';
        const defaultRange = isGateway ? 7 * 24 * 3600 : 90 * 24 * 3600;
        // Use asset's firstCheckedIn as start if it's older than the default range,
        // so we always fetch available history even for devices that haven't reported recently
        const defaultStart = now - defaultRange;
        const assetStart = foundAsset.firstCheckedIn
          ? (typeof foundAsset.firstCheckedIn === 'number' ? foundAsset.firstCheckedIn : Math.floor(new Date(foundAsset.firstCheckedIn).getTime() / 1000))
          : defaultStart;
        dispatch(fetchAssetHistory({
          assetId: id,
          startTs: Math.min(defaultStart, assetStart),
          endTs: now,
        }));
      }
    };
    loadAsset();
  }, [dispatch, id, assetList, projectAssetList]);

  // Load lookups for edit
  useEffect(() => {
    if (assetTypeList.length === 0) dispatch(fetchAssetTypes());
    if (assetSubtypeList.length === 0) dispatch(fetchAssetSubTypes());
    if (projectList.length === 0 && activeCustomer?.id) dispatch(fetchProjectsByCustomer(activeCustomer.id));
    api.listAssetOwners().then(r => setAssetOwners(r.items || [])).catch(() => {});
  }, [dispatch, assetTypeList.length, assetSubtypeList.length, projectList.length, activeCustomer?.id]);

  // Set asset position on map + zoom
  useEffect(() => {
    if (!asset) return;
    const coord = asset.coordinate || asset.location;
    if (!coord) return;

    const lat = coord.latitude ?? coord.lat;
    const lng = coord.longitude ?? coord.lng;
    if (lat == null || lng == null) return;

    dispatch(setAssetsData({
      [asset.id]: {
        id: asset.id,
        unitNumber: safeGet('unitNumber', asset),
        name: asset.name,
        type: asset.type,
        subType: asset.subType,
        assetTypeName: asset.type?.name || '',
        coordinates: { latitude: lat, longitude: lng },
        status: asset.movingStatus || 'idle',
        lastReceived: formatDateTime(asset.receivedTs, 'M/D/YY h:mmA'),
      },
    }));

    dispatch(updateViewport({ latitude: lat, longitude: lng, zoom: 15 }));
  }, [dispatch, asset]);

  // Load project overlays for the asset's project
  useEffect(() => {
    if (!asset?.projectId) return;
    const loadOverlays = async () => {
      try {
        const projectResult = await api.getProject(asset.projectId);
        const project = projectResult.item;
        if (project?.boundaries) {
          dispatch(setProjectBoundariesData({
            [project.id]: { id: project.id, boundaries: project.boundaries, name: project.name },
          }));
        }
        const areasResult = await api.getProjectAreas(asset.projectId);
        const areas = areasResult.items || [];
        setProjectAreas(areas);
        const overlays = {};
        for (const area of areas) {
          if (area.geoJson) {
            overlays[area.id] = {
              id: area.id,
              name: area.name || area.areaTypeName,
              areaTypeId: area.areaTypeId,
              areaTypeName: area.areaTypeName,
              geoJson: area.geoJson,
            };
          }
        }
        dispatch(setOverlaysData(overlays));
      } catch {
        // fall through — overlays just won't render
      }
    };
    loadOverlays();
  }, [dispatch, asset?.projectId]);

  // Update waypoints on map when timeline changes
  useEffect(() => {
    if (visibleWaypoints.length === 0) {
      dispatch(setMovesData({}));
      return;
    }
    const movesData = {};
    visibleWaypoints.forEach((wp, i) => {
      movesData[`wp-${i}`] = {
        id: `wp-${i}`,
        coordinates: { latitude: wp.latitude, longitude: wp.longitude },
        type: wp.type,
        timestamp: wp.timestamp,
      };
    });
    dispatch(setMovesData(movesData));

    // Fit map bounds to show all waypoints when they span a significant area
    if (visibleWaypoints.length >= 2) {
      const lats = visibleWaypoints.map(wp => wp.latitude);
      const lngs = visibleWaypoints.map(wp => wp.longitude);
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);
      const latSpan = maxLat - minLat;
      const lngSpan = maxLng - minLng;

      // Only adjust viewport if waypoints span more than ~0.005 degrees (~500m)
      if (latSpan > 0.005 || lngSpan > 0.005) {
        const centerLat = (minLat + maxLat) / 2;
        const centerLng = (minLng + maxLng) / 2;
        // Estimate zoom level from span (rough heuristic)
        const maxSpan = Math.max(latSpan, lngSpan);
        const zoom = Math.max(8, Math.min(15, Math.floor(9 - Math.log2(maxSpan))));
        dispatch(updateViewport({ latitude: centerLat, longitude: centerLng, zoom }));
      }
    }
  }, [dispatch, visibleWaypoints]);

  // Edit dialog handlers
  const openEditDialog = useCallback(() => {
    setFormData({
      name: asset?.name || '',
      description: asset?.description || '',
      status: asset?.status || 'active',
      unitNumber: asset?.unitNumber || '',
      assetTypeId: asset?.assetTypeId || asset?.type?.id || '',
      assetSubTypeId: asset?.assetSubTypeId || asset?.subType?.id || '',
      projectId: asset?.projectId || '',
      make: asset?.make || '',
      model: asset?.model || '',
      year: asset?.year || '',
      supplier: asset?.supplier || '',
      dimensions: asset?.dimensions || '',
      owner: asset?.owner || '',
      deviceType: asset?.deviceType || '',
      capacity: asset?.capacity || '',
      annualInspectionDate: asset?.annualInspectionDate || '',
    });
    setEditDialogOpen(true);
  }, [asset]);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== (asset[key] || '')) updates[key] = formData[key];
      });
      if (Object.keys(updates).length > 0) {
        await dispatch(updateAssetAction({ id: asset.id, data: updates })).unwrap();
        setAsset(prev => ({ ...prev, ...updates }));
        setSnackbar({ open: true, message: 'Asset updated successfully', severity: 'success' });
      }
      setEditDialogOpen(false);
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to update asset', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Resolve type/subtype names
  const typeName = useMemo(() => {
    if (asset?.type?.name) return asset.type.name;
    const found = assetTypeList.find(t => t.id === asset?.assetTypeId);
    return found?.name || '';
  }, [asset, assetTypeList]);

  const subtypeName = useMemo(() => {
    if (asset?.subType?.name) return asset.subType.name;
    const found = assetSubtypeList.find(s => s.id === asset?.assetSubTypeId);
    return found?.name || '';
  }, [asset, assetSubtypeList]);

  // Detail field helper
  const DetailRow = ({ label, value }) => (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.5px' }}>
        {label}
      </Typography>
      <Typography variant="body2">{value || '-'}</Typography>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <AppBar
          showProjectSelector
          projectSelectorProps={{
            ProjectSelectorComponent,
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (!asset) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <AppBar
          showProjectSelector
          projectSelectorProps={{
            ProjectSelectorComponent,
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, flexDirection: 'column', gap: 2 }}>
          <Typography variant="h5" color="text.secondary">Asset not found</Typography>
          <Button variant="contained" startIcon={<BackIcon />} onClick={() => navigate(-1)}>Go Back</Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* App Bar - will show current project name or title */}
      <AppBar
        showProjectSelector
        projectSelectorProps={{
          ProjectSelectorComponent,
        }}
      />

      {/* Main: Map + Sidebar */}
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden', position: 'relative' }}>
        {/* Floating Back Button - positioned below AppBar */}
        <Button
          variant="contained"
          size="small"
          startIcon={<BackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            position: 'absolute',
            top: 20,
            left: 20,
            zIndex: 10,
            textTransform: 'none',
            fontSize: '0.75rem',
            backgroundColor: colorPalette.backgroundColor,
            '&:hover': {
              backgroundColor: colorPalette.surfaceBright || '#4A4948',
            },
          }}
        >
          BACK
        </Button>

        {/* Map */}
        <Box sx={{ flexGrow: 1, position: 'relative' }}>
          <AsseTracMap visibleOverlays={visibleOverlays} showProjectMarkers={false} />
        </Box>

        {/* Detail Sidebar */}
        <Box
          sx={{
            width: SIDEBAR_WIDTH,
            borderLeft: `1px solid ${colorPalette.lightSteel}`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            flexShrink: 0,
          }}
        >
          {/* Header: Unit number, type, edit icon */}
          <Box sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            p: 2,
            pb: 1,
          }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{ color: colorPalette.portlandCement, fontWeight: 600, fontSize: '18px', lineHeight: 1.3 }}
              >
                {asset.name || asset.unitNumber || 'Asset'}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', fontSize: '12px', mt: 0.25 }}
              >
                {asset.unitNumber || asset.id}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {typeName}{subtypeName ? ` / ${subtypeName}` : ''}
              </Typography>
              {asset.cleanStatus === 'dirty' && (
                <Chip label="DIRTY" size="small" sx={{ backgroundColor: colorPalette.error, color: 'white', fontWeight: 700, fontSize: '0.65rem', ml: 1 }} />
              )}
            </Box>
            <IconButton
              onClick={openEditDialog}
              aria-label="Edit asset"
              sx={{
                borderRadius: '8px',
                color: colorPalette.portlandCement,
                padding: '8px',
                flexShrink: 0,
                mt: 0,
                width: 'auto',
                height: 'auto',
                backgroundColor: 'transparent',
                '&:hover': { backgroundColor: 'rgba(232, 220, 218, 0.08)' },
              }}
            >
              <EditIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          {/* Tabs: DETAILS | OVERLAYS */}
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{ borderBottom: `1px solid ${colorPalette.lightSteel}` }}
          >
            <Tab label="DETAILS" sx={{ fontSize: '0.75rem' }} />
            <Tab label="OVERLAYS" sx={{ fontSize: '0.75rem' }} />
          </Tabs>

          {/* Tab Content */}
          {activeTab === 0 ? (
            /* Details Tab */
            <Box sx={{ p: 2, flexGrow: 1, overflow: 'auto' }}>
              <DetailRow label="DESCRIPTION" value={asset.description} />
              <DetailRow label="SUPPLIER" value={asset.supplier} />
              <DetailRow label="DIMENSIONS" value={asset.dimensions} />
              <DetailRow label="YEAR" value={asset.year} />
              <DetailRow label="UUID" value={asset.uuid} />
              <DetailRow label="DEVICE TYPE" value={asset.deviceType || 'Beacon'} />
              <DetailRow label="VERSION" value={asset.version} />

              <Divider sx={{ my: 2 }} />

              <DetailRow label="ACTIVATED ON" value={formatDateTime(asset.createdAt)} />
              <DetailRow label="LAST CHECK IN" value={formatDateTime(asset.receivedTs)} />
              <DetailRow label="LAST REPORT" value={formatDateTime(asset.eventTs || asset.receivedTs)} />

              <Divider sx={{ my: 2 }} />

              <Button
                variant="outlined"
                size="small"
                fullWidth
                onClick={() => setRetireDialogOpen(true)}
                disabled={asset.status === 'retired'}
                sx={{ mb: 1, textTransform: 'uppercase', fontSize: '0.7rem' }}
              >
                {asset.status === 'retired' ? 'RETIRED' : 'RETIRE ASSET'}
              </Button>
            </Box>
          ) : (
            /* Overlays Tab - Show Map Overlays Panel */
            <Box sx={{ flexGrow: 1, overflow: 'auto', position: 'relative' }}>
              <MapOverlaysPanel
                open={true}
                onClose={() => {}}
                visibleOverlays={visibleOverlays}
                onToggleOverlay={(oid) => dispatch(toggleOverlay(oid))}
                onToggleAll={(checked) => {
                  dispatch(setVisibleOverlays(checked ? projectAreas.map(a => a.id) : []));
                }}
                projectAreas={projectAreas}
                embedded={true}
              />
            </Box>
          )}


        </Box>
      </Box>

      {/* Timeline Slider (bottom) */}
      <AssetTimeline
        historyList={assetHistoryList}
        deviceType={asset?.deviceType || 'Beacon'}
        onRangeChange={(range) => {
          const startTs = range.from ? Math.floor(new Date(range.from).getTime() / 1000) : undefined;
          const endTs = range.to ? Math.floor(new Date(range.to).getTime() / 1000) : Math.floor(Date.now() / 1000);
          dispatch(fetchAssetHistory({ assetId: id, startTs, endTs }));
        }}
        onWaypointsChange={setVisibleWaypoints}
      />

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: '1.25rem', fontWeight: 600, textTransform: 'uppercase' }}>Edit Asset</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField label="Name" fullWidth value={formData.name || ''} onChange={(e) => handleChange('name', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Unit Number" fullWidth value={formData.unitNumber || ''} onChange={(e) => handleChange('unitNumber', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Asset Type</InputLabel>
                <Select value={formData.assetTypeId || ''} label="Asset Type" onChange={(e) => handleChange('assetTypeId', e.target.value)}>
                  <MenuItem value=""><em>None</em></MenuItem>
                  {assetTypeList.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Subtype</InputLabel>
                <Select value={formData.assetSubTypeId || ''} label="Subtype" onChange={(e) => handleChange('assetSubTypeId', e.target.value)}>
                  <MenuItem value=""><em>None</em></MenuItem>
                  {assetSubtypeList.filter(s => !formData.assetTypeId || s.typeId === formData.assetTypeId).map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Project</InputLabel>
                <Select value={formData.projectId || ''} label="Project" onChange={(e) => handleChange('projectId', e.target.value)}>
                  <MenuItem value=""><em>Unassigned</em></MenuItem>
                  {projectList.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={formData.status || ''} label="Status" onChange={(e) => handleChange('status', e.target.value)}>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}><TextField label="Make" fullWidth value={formData.make || ''} onChange={(e) => handleChange('make', e.target.value)} /></Grid>
            <Grid item xs={4}><TextField label="Model" fullWidth value={formData.model || ''} onChange={(e) => handleChange('model', e.target.value)} /></Grid>
            <Grid item xs={4}><TextField label="Year" fullWidth value={formData.year || ''} onChange={(e) => handleChange('year', e.target.value)} /></Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel>Supplier</InputLabel>
                <Select value={formData.supplier || ''} label="Supplier" onChange={(e) => handleChange('supplier', e.target.value)}>
                  <MenuItem value=""><em>Select</em></MenuItem>
                  {uniqueSuppliers.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel>Dimensions</InputLabel>
                <Select value={formData.dimensions || ''} label="Dimensions" onChange={(e) => handleChange('dimensions', e.target.value)}>
                  <MenuItem value=""><em>Select</em></MenuItem>
                  {uniqueDimensions.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel>Owner</InputLabel>
                <Select value={formData.owner || ''} label="Owner" onChange={(e) => handleChange('owner', e.target.value)}>
                  <MenuItem value=""><em>Select</em></MenuItem>
                  {[...new Set([...assetOwners.map(o => o.name).filter(Boolean), ...uniqueOwnerNames])].sort().map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel>Device Type</InputLabel>
                <Select value={formData.deviceType || 'Beacon'} label="Device Type" onChange={(e) => handleChange('deviceType', e.target.value)}>
                  <MenuItem value="Beacon">Beacon</MenuItem>
                  <MenuItem value="Gateway">Gateway</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}><TextField label="Capacity" fullWidth value={formData.capacity || ''} onChange={(e) => handleChange('capacity', e.target.value)} /></Grid>
            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Annual Inspection Date"
                  value={formData.annualInspectionDate ? dayjs(formData.annualInspectionDate) : null}
                  onChange={(newValue) => handleChange('annualInspectionDate', newValue ? newValue.format('YYYY-MM-DD') : '')}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}><TextField label="Description" fullWidth multiline rows={2} value={formData.description || ''} onChange={(e) => handleChange('description', e.target.value)} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={saving} variant="text" aria-label="Cancel edit">Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={saving} 
            aria-label="Save asset changes" 
            sx={{ 
              backgroundColor: colorPalette.asseTracOrange,
              '&:hover': { backgroundColor: (theme) => theme.palette.primary.dark }
            }}
          >
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Retire Dialog */}
      <RetireAssetDialog
        open={retireDialogOpen}
        onClose={() => setRetireDialogOpen(false)}
        asset={asset}
        onRetired={() => {
          setAsset(prev => ({ ...prev, status: 'retired' }));
          setSnackbar({ open: true, message: 'Asset retired successfully', severity: 'success' });
        }}
      />

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AssetPage;
